import {
  BasicModal,
  Box,
  Button,
  Chip,
  Icon,
  Theme,
  Typography
} from "@outschool/backpack";
import { centsToDollars } from "@outschool/business-rules";
import {
  ResumeSubscriptionMutation,
  ResumeSubscriptionMutationVariables
} from "@outschool/gql-frontend-generated";
import { faArrowRight } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import {
  addParamsToUrl,
  homePath,
  joinMembershipPath
} from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  useApproximateMonthlySpendFromActiveEnrollments,
  useConfetti,
  useIsEligibleFor40PercentCouponUpsell,
  useRequiresMembershipForEnrollment,
  useSubscriptionPlans
} from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import { lazyWithRetry, useNavigation } from "@outschool/ui-utils";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from "@stripe/react-stripe-js";
import React from "react";
import { useLocation } from "react-router-dom";

import * as Env from "../../../shared/Env";
import { useSubscriptionCreditBalance } from "../../hooks/useSubscriptionCreditBalance";
import useCDPMembershipModal from "../detailPage/hooks/useCDPMembershipModal";
import { resumeSubscriptionMutation } from "../subscriptions/hooks/resumeSubscription";
import {
  checkSessionStatusMutation,
  createStripeCheckoutSessionMutation
} from "../subscriptions/mutations";
import { stripePromise } from "./Stripe";

const EsaMembershipAddCredits = lazyWithRetry(async () => {
  const module = await import(
    /* webpackChunkName: "subscriptions" */ "../esaMemberships/EsaMembershipAddCreditsModal"
  );

  return {
    default: module.EsaMembershipAddCredits
  };
});

// 40% off, so fractional discount is 0.4
const FRACTIONAL_DISCOUNT_FOR_40_PERCENT_COUPON = 0.4;

enum STATUS {
  SELECT_PLAN,
  RESUME_PLAN,
  LOADING_CLIENT_SECRET,
  EMBEDDED_CHECKOUT,
  PROVISIONING_SUBSCRIPTION,
  PROVISIONING_SUCCESS,
  POLLING_TIMEOUT,
  PAYMENT_FAILED,
  ADD_ESA_MEMBERSHIP
}

type JoinMembershipModalStateType = {
  status: STATUS;
  clientSecret: string | null;
  selectedPlan: string | null;
  isModalOpen: boolean;
  pollForSubscription: boolean;
};

const initialState: JoinMembershipModalStateType = {
  status: STATUS.SELECT_PLAN,
  clientSecret: null,
  selectedPlan: null,
  isModalOpen: false,
  pollForSubscription: false
};

function reducer(
  state: JoinMembershipModalStateType,
  action: {
    type: string;
    selectedPlan?: string | null | undefined;
    clientSecret?: string | null | undefined;
    isModalOpen?: boolean;
    pollForSubscription?: boolean;
  }
) {
  switch (action.type) {
    case "OPEN_MODAL":
      return { ...state, isModalOpen: true };
    case "CLOSE_MODAL":
    case "ADD_ESA_MEMBERSHIP":
      return { ...state, status: STATUS.ADD_ESA_MEMBERSHIP };
    case "RESET":
      return initialState;
    case "SET_PLAN":
      return { ...state, selectedPlan: action.selectedPlan };
    case "RESUME_PLAN":
      return { ...state, status: STATUS.RESUME_PLAN };
    case "LOADING_CLIENT_SECRET":
      return { ...state, status: STATUS.LOADING_CLIENT_SECRET };
    case "SET_CLIENT_SECRET":
      return {
        ...state,
        clientSecret: action.clientSecret,
        status: STATUS.EMBEDDED_CHECKOUT
      };
    case "PROVISIONING_SUBSCRIPTION":
      return {
        ...state,
        status: STATUS.PROVISIONING_SUBSCRIPTION,
        pollForSubscription: true
      };
    case "PROVISIONING_SUCCESS":
      return {
        ...state,
        status: STATUS.PROVISIONING_SUCCESS,
        pollForSubscription: false
      };
    case "POLLING_TIMEOUT":
      return { ...state, status: STATUS.POLLING_TIMEOUT };
    case "PAYMENT_FAILED":
      return {
        ...state,
        clientSecret: null,
        selectedPlan: null,
        status: STATUS.PAYMENT_FAILED
      };
    default:
      return state;
  }
}

const MembershipResumeContent = ({
  afterResume
}: {
  afterResume: () => void;
}) => {
  const { t } = useTranslation("client\\components\\payments\\Stripe");
  const trackEvent = useTrackEvent();
  const { willResumeAt } = useSubscriptionCreditBalance();

  const [resumeSubscription] = useMutation<
    ResumeSubscriptionMutation,
    ResumeSubscriptionMutationVariables
  >(resumeSubscriptionMutation);

  return (
    <Box
      sx={{
        paddingRight: "2em"
      }}
    >
      <Typography variant="h3" sx={{ paddingRight: "2em" }}>
        {t("Resume Your Membership")}
      </Typography>
      <Box
        sx={{
          marginY: 24
        }}
      >
        <Typography variant="body1" sx={{ display: "block" }}>
          {t("Your membership is set to resume on {{resumeDate}}.", {
            resumeDate: dayjs(willResumeAt).format("MM/DD/YYYY")
          })}
        </Typography>
        <Typography variant="body1" sx={{ marginTop: 24, display: "block" }}>
          {t(
            "You can resume at any time to regain access to your credits and membership perks. You can also update your pause period here."
          )}
        </Typography>
      </Box>
      <Box>
        <Button
          variant="contained"
          onClick={() => {
            trackEvent("JOIN_MEMBERSHIPS_RESUME_MEMBERSHIP");
            resumeSubscription();
            afterResume();
          }}
        >
          {t("Resume Now to Enroll")}
        </Button>
      </Box>
    </Box>
  );
};

export const JoinMembershipModal = () => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const { confettiCannon, fireConfetti } = useConfetti({
    spread: 180,
    elementCount: 100
  });
  const { currentUser, currentUserIsLoading, currentUserRefetch, isLoggedIn } =
    useSession();

  const {
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment
  } = useRequiresMembershipForEnrollment();
  const { t } = useTranslation("client\\components\\payments\\Stripe");
  const location = useLocation();
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const query = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const { loading: subscriptionPlansLoading, subscriptionPlans } =
    useSubscriptionPlans();

  const redirectAfterPath = query.get("redirectAfterPath");
  const isEnrolling =
    location.pathname.includes("/classes/") ||
    location.pathname.includes("/groups/");
  const WELCOME30DISCOUNTCENTS = 3000;

  const { isEligibleFor40PercentCouponUpsell } =
    useIsEligibleFor40PercentCouponUpsell();

  // Approximate monthly spend for logged-in existing buyers who are enrolling so we can recommend a plan, skip otherwise
  const skipMonthlySpendApproximation =
    !isLoggedIn ||
    !isEnrolling ||
    (!currentUserIsLoading && !currentUser?.firstPaidEnrollment);

  const approxMonthlySpendingAmount =
    useApproximateMonthlySpendFromActiveEnrollments(
      skipMonthlySpendApproximation
    );

  const recommendedPlan = React.useMemo<
    | ReturnType<typeof useSubscriptionPlans>["subscriptionPlans"][number]
    | undefined
  >(() => {
    if (subscriptionPlansLoading) {
      return undefined;
    }

    if (isEnrolling && isEligibleFor40PercentCouponUpsell) {
      // If user is enrolling in a class and they are an existing buyer who is eligible for the 40PERCENT coupon upsell modal,
      // then recommend a plan based on their approximate monthly spending
      if (
        subscriptionPlans.length > 0 &&
        approxMonthlySpendingAmount * 100 >
          (subscriptionPlans.at(-1)?.amountCents ?? 0)
      ) {
        // If monthly spending is greater than the most expensive plan, just recommend most expensive plan
        return subscriptionPlans.at(-1)!;
      } else {
        // Otherwise, recommend the cheapest plan that is greater than or equal to their monthly spending
        return (
          subscriptionPlans.find(
            plan => plan.amountCents >= approxMonthlySpendingAmount * 100
          ) || subscriptionPlans[0]
        );
      }
    } else {
      // For all other cases, just recommend the cheapest plan
      return subscriptionPlans[0];
    }
  }, [
    subscriptionPlansLoading,
    subscriptionPlans,
    isEnrolling,
    isEligibleFor40PercentCouponUpsell,
    approxMonthlySpendingAmount
  ]);

  const isRecommendingCheapestPlan = recommendedPlan === subscriptionPlans[0];

  const utmSource = query.get("utm_source");
  const utmMedium = query.get("utm_medium");
  const utmCampaign = query.get("utm_campaign");

  const timeout = 20000;
  const pollingInterval = 3000;
  usePolling({
    shouldStart: state.pollForSubscription,
    onTick: currentUserRefetch,
    onTimeout: () => {
      dispatch({ type: "POLLING_TIMEOUT" });
    },
    timeout,
    interval: pollingInterval
  });

  const [createStripeCheckoutSession] = useMutation(
    createStripeCheckoutSessionMutation
  );

  const [checkSessionStatus] = useMutation(checkSessionStatusMutation);

  const embeddedCheckoutOptions = React.useMemo(
    () => ({
      clientSecret: state.clientSecret,
      onComplete: async () => {
        try {
          const { data } = await checkSessionStatus({
            variables: { clientSecret: state.clientSecret }
          });
          if (data.checkSessionStatus.paymentStatus === "paid") {
            dispatch({ type: "PROVISIONING_SUBSCRIPTION" });
          } else {
            dispatch({ type: "PAYMENT_FAILED" });
          }
        } catch {
          // Failed to verify payment status, marking as timeout to display message to user to contact support
          dispatch({ type: "POLLING_TIMEOUT" });
        }
      }
    }),
    [state.clientSecret, checkSessionStatus]
  );

  const fetchClientSecret = React.useCallback(
    async (plan: string) => {
      const { data } = await createStripeCheckoutSession({
        variables: {
          plan: plan,
          onSuccessPath: redirectAfterPath ?? homePath(),
          isEmbedded: true
        }
      });
      dispatch({
        type: "SET_CLIENT_SECRET",
        clientSecret: data.createStripeCheckoutSession
      });
    },
    [redirectAfterPath, createStripeCheckoutSession]
  );

  const { snoozeCDPMembershipModal } = useCDPMembershipModal();

  const onClose = React.useCallback(() => {
    if (state.status !== STATUS.ADD_ESA_MEMBERSHIP) {
      dispatch({ type: "RESET" });
    }
    const params = new URLSearchParams(location.search);
    // eslint-disable-next-line i18next/no-literal-string
    params.delete("joinMembershipModal");
    // eslint-disable-next-line i18next/no-literal-string
    params.delete("plan");

    navigate(`${location.pathname}?${params.toString()}`, {
      hardNav: true,
      replace: true
    });
  }, [navigate, location.pathname, location.search, dispatch, state.status]);

  React.useEffect(() => {
    if (
      currentUser &&
      requiresMembershipForEnrollment &&
      currentUser.hasSubscription &&
      !currentUser.canPayWithSubscriptionCredits
    ) {
      dispatch({ type: "RESUME_PLAN" });
    }
  }, [currentUser, requiresMembershipForEnrollment]);

  React.useEffect(() => {
    const plan = query.get("plan");
    const shouldOpenModal =
      query.get("joinMembershipModal") === "true" && isLoggedIn;
    if (shouldOpenModal && !state.isModalOpen) {
      dispatch({ type: "OPEN_MODAL" });
      trackEvent("JOIN_MEMBERSHIPS_MODAL_VIEW", {
        plan: recommendedPlan?.uid,
        userId: currentUser?.uid,
        utmCampaign,
        utmSource,
        utmMedium,
        requiresMembershipForEnrollment
      });
    }
    if (
      Boolean(plan) &&
      shouldOpenModal &&
      ((plan && subscriptionPlans.some(p => p.uid === plan)) ||
        state.selectedPlan) &&
      !state.clientSecret &&
      state.status !== STATUS.LOADING_CLIENT_SECRET
    ) {
      dispatch({ type: "LOADING_CLIENT_SECRET" });
      fetchClientSecret((plan || state.selectedPlan) as string);
    }
  }, [
    isLoggedIn,
    query,
    state.selectedPlan,
    state.isModalOpen,
    fetchClientSecret,
    trackEvent,
    recommendedPlan?.uid,
    currentUser?.uid,
    utmCampaign,
    utmSource,
    utmMedium,
    subscriptionPlans,
    state.clientSecret,
    state.status,
    requiresMembershipForEnrollment
  ]);

  React.useEffect(() => {
    if (
      currentUser?.hasSubscription &&
      currentUser?.canPayWithSubscriptionCredits &&
      state.status === STATUS.PROVISIONING_SUBSCRIPTION
    ) {
      dispatch({ type: "PROVISIONING_SUCCESS" });

      const planUid = query.get("plan") || state.selectedPlan;

      const plan = subscriptionPlans.find(p => p.uid === planUid);

      if (plan) {
        trackAdEvent("New Buyer Acquired", {
          revenue: plan.amountCents,
          total: plan.amountCents,
          currency: "USD",
          isSubscription: true,
          subscriptionName: plan.uid
        });

        trackAdEvent("New Buyer Membership Client", {
          plan: plan.uid,
          revenue: plan.amountCents,
          numCredits: plan.numCredits,
          currency: "USD",
          userId: currentUser?.uid,
          utmCampaign,
          utmSource,
          utmMedium
        });
      }
    }

    if (state.status === STATUS.PROVISIONING_SUCCESS) {
      const confettiTimeOutId = setTimeout(() => {
        fireConfetti();
      }, 500);

      const resetTimeoutId = setTimeout(() => {
        if (isEnrolling && !redirectAfterPath) {
          onClose();
        } else {
          dispatch({ type: "RESET" });
          if (redirectAfterPath) {
            navigate(redirectAfterPath, { replace: true });
          } else {
            const params = new URLSearchParams(location.search);
            const newParams: Record<string, string | boolean> = {};
            if (params.get("openConcierge") === "true") {
              newParams.openConcierge = true;
            }
            navigate(addParamsToUrl(homePath(), newParams), { replace: true });
          }
        }
      }, 4000);

      return () => {
        clearTimeout(confettiTimeOutId);
        clearTimeout(resetTimeoutId);
      };
    }
    return;
  }, [
    location.search,
    currentUser,
    fireConfetti,
    navigate,
    redirectAfterPath,
    state.status,
    state.selectedPlan,
    query,
    trackAdEvent,
    utmCampaign,
    utmSource,
    utmMedium,
    isEnrolling,
    onClose,
    subscriptionPlans
  ]);

  React.useEffect(() => {
    if (
      currentUser &&
      currentUser.requiresMembershipForEnrollment &&
      currentUser.hasSubscription &&
      !currentUser.canPayWithSubscriptionCredits
    ) {
      dispatch({ type: "RESUME_PLAN" });
    }
  }, [currentUser]);

  React.useEffect(() => {
    if (requiresEsaMembershipForEnrollment) {
      dispatch({ type: "ADD_ESA_MEMBERSHIP" });
    }
  }, [requiresEsaMembershipForEnrollment]);

  const couponSavingsCents = isEligibleFor40PercentCouponUpsell
    ? (recommendedPlan?.amountCents ?? 0) *
      FRACTIONAL_DISCOUNT_FOR_40_PERCENT_COUPON
    : WELCOME30DISCOUNTCENTS;

  const renderBody = () => {
    if (!recommendedPlan) {
      return <Loading />;
    }
    switch (state.status) {
      case STATUS.LOADING_CLIENT_SECRET:
        return (
          <Box sx={{ paddingRight: "2em" }}>
            <Loading />
          </Box>
        );

      case STATUS.EMBEDDED_CHECKOUT:
        return (
          <Box sx={{ width: "100%", paddingRight: "2em" }}>
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={embeddedCheckoutOptions}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </Box>
        );

      case STATUS.PROVISIONING_SUBSCRIPTION:
      case STATUS.POLLING_TIMEOUT:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {t`Processing your membership.`}
            </Typography>
            <Typography variant="body" sx={{ textAlign: "center" }}>
              {state.status === STATUS.PROVISIONING_SUBSCRIPTION
                ? t`Please do not close this browser until your transaction is complete.`
                : t`We are experiencing technical difficulties. Please contact support`}
            </Typography>
            {state.status === STATUS.PROVISIONING_SUBSCRIPTION && <Loading />}
          </Box>
        );
      case STATUS.PROVISIONING_SUCCESS:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32,
              position: "relative",
              overflow: "hidden",
              minHeight: 400
            }}
          >
            {confettiCannon && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)"
                }}
              >
                {confettiCannon}
              </Box>
            )}
            <Typography variant="h3" sx={{ fontWeight: "bold" }}>
              {t`Congrats! You are in!`}
            </Typography>
          </Box>
        );

      case STATUS.PAYMENT_FAILED:
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              alignItems: "center",
              justifyContent: "center",
              paddingRight: "2em",
              margin: 32
            }}
          >
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>
              {t`Payment Failed`}
            </Typography>
            <Typography variant="body" sx={{ textAlign: "center" }}>
              {t`We were unable to process your payment. Please try again or use a different payment method.`}
            </Typography>
            <TrackedButton
              size="large"
              trackingName="join_memberships_payment_retry_button"
              variant="contained"
              onClick={() => dispatch({ type: "RESET" })}
            >
              {t`Try Again`}
            </TrackedButton>
          </Box>
        );

      case STATUS.RESUME_PLAN:
        return (
          <MembershipResumeContent
            afterResume={() => {
              dispatch({ type: "PROVISIONING_SUBSCRIPTION" });
            }}
          />
        );

      case STATUS.ADD_ESA_MEMBERSHIP:
        return (
          <Box sx={{ paddingRight: "2rem" }}>
            <EsaMembershipAddCredits esaCreditBalances={[]} onClose={onClose} />
          </Box>
        );

      // STATUS.SELECT_PLAN
      default:
        return (
          <JoinMembershipEnrollmentContent
            title={
              requiresMembershipForEnrollment
                ? t("Membership required to enroll")
                : t("Become a Member and Start Saving!")
            }
            description={
              isEligibleFor40PercentCouponUpsell ? (
                requiresMembershipForEnrollment ? (
                  t(
                    "Outschool is moving to Memberships. Each month, receive credits to spend on any class. Join now to save 40% on your first month! 🎉"
                  )
                ) : isRecommendingCheapestPlan ? (
                  <Trans t={t}>
                    <Typography variant="body1">
                      Get <b>40% off your first month</b> plus bonus credits
                      built into your plan — together, that’s up to 50% in total
                      savings. You can always upgrade or add more credits as
                      your learning needs grow.
                    </Typography>
                  </Trans>
                ) : (
                  <Trans t={t}>
                    <Typography variant="body1">
                      <b>
                        We’ve selected this plan based on your typical monthly
                        enrollments
                      </b>
                      , so you can start saving right away.{" "}
                      <b>Get 40% off your first month</b>, plus bonus credits
                      built into your plan — that’s up to 50% in total savings.
                    </Typography>
                  </Trans>
                )
              ) : (
                t(
                  "Join a membership to enroll in classes on Outschool. Join today and get up to 50% more credits to spend on this class and more! 🎉"
                )
              )
            }
            couponCode={
              isEligibleFor40PercentCouponUpsell ? "40PERCENT" : "WELCOME30"
            }
            couponSavingsCents={couponSavingsCents}
            requiresMembershipForEnrollment={requiresMembershipForEnrollment}
            recommendedPlan={recommendedPlan}
            onJoinPlan={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              if (Env.IS_READ_ONLY_MODE) {
                return;
              }
              trackEvent("JOIN_MEMBERSHIPS_PLAN_SELECTED", {
                plan: recommendedPlan.uid,
                userId: currentUser?.uid,
                utmCampaign,
                utmSource,
                utmMedium,
                requiresMembershipForEnrollment
              });
              dispatch({
                type: "SET_PLAN",
                selectedPlan: recommendedPlan.uid
              });
              dispatch({ type: "LOADING_CLIENT_SECRET" });
              fetchClientSecret(recommendedPlan.uid);
            }}
            onContinueWithoutPlan={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              snoozeCDPMembershipModal();
              if (redirectAfterPath) {
                dispatch({ type: "RESET" });
                navigate(redirectAfterPath, { replace: true });
              } else {
                onClose();
              }
            }}
            onMorePlans={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              navigate(
                joinMembershipPath({
                  ...(redirectAfterPath ? { redirectAfterPath } : undefined)
                }),
                {
                  hardNav: true,
                  replace: true
                }
              );
            }}
          />
        );
    }
  };

  return (
    <BasicModal
      open={state.isModalOpen}
      onClose={onClose}
      hasCloseButton={true}
      closeButtonArialLabel={t("Close")}
      sx={{ paddingRight: 0, overflow: "auto" }}
      modalCloseButtonProps={{
        // eslint-disable-next-line i18next/no-literal-string
        size: "large",
        sx: {
          fontSize: 24
        }
      }}
    >
      <Box data-test-id="join-membership-modal-content">{renderBody()}</Box>
    </BasicModal>
  );
};

function JoinMembershipEnrollmentContent({
  title,
  description,
  couponCode,
  couponSavingsCents,
  requiresMembershipForEnrollment,
  recommendedPlan,
  onJoinPlan,
  onContinueWithoutPlan,
  onMorePlans
}: {
  title: string;
  description: JSX.Element | string;
  couponCode: string;
  couponSavingsCents: number;
  requiresMembershipForEnrollment: boolean;
  recommendedPlan: ReturnType<
    typeof useSubscriptionPlans
  >["subscriptionPlans"][number];
  onJoinPlan: (e: React.MouseEvent<HTMLElement>) => void;
  onContinueWithoutPlan: (e: React.MouseEvent<HTMLElement>) => void;
  onMorePlans: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const { t } = useTranslation("client\\components\\payments\\Stripe");
  return (
    <Box
      flex
      sx={{
        flexDirection: "column",
        paddingRight: "2em",
        gap: 24
      }}
    >
      <Box flex sx={{ flexDirection: "column", gap: 24 }}>
        <Typography emphasized variant="h4">
          {title}
        </Typography>
        {description}
      </Box>
      <Box
        sx={{
          paddingX: 16,
          paddingY: 20,
          border: "1px solid var(--border-border-neutral-lighter, #E0E0E0)",
          borderRadius: 16
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: 8, fontSize: 18 }}>
          {recommendedPlan.numCredits + " " + t("credits")}
        </Typography>
        <Box sx={{ marginBottom: 8 }}>
          <Typography
            as="span"
            variant="display2"
            sx={{
              textDecoration: "line-through",
              fontWeight: 400
            }}
          >
            {`$${centsToDollars(recommendedPlan.amountCents)}`}
          </Typography>{" "}
          <Typography
            as="span"
            variant="display2"
            sx={{
              fontWeight: "bold"
            }}
          >
            {`$${centsToDollars(
              recommendedPlan.amountCents - couponSavingsCents
            )}`}
          </Typography>{" "}
          <Typography as="span" variant="subtitle1">
            {t("USD / month")}
          </Typography>
        </Box>
        <Chip
          color="warning"
          size="medium"
          label={t("First month only – code {{couponCode}}", {
            couponCode
          })}
          sx={{
            height: "auto",
            padding: "2px",
            "& .MuiChip-label": {
              whiteSpace: "normal",
              overflow: "visible",
              textOverflow: "clip",
              lineHeight: "1.4",
              padding: "4px 8px"
            }
          }}
        />
        <Box sx={{ marginTop: 24, gap: 24, display: "flex" }}>
          {!requiresMembershipForEnrollment && (
            <TrackedButton
              size="small"
              variant="link"
              color="inherit"
              onClick={onMorePlans}
            >
              {t`More Plans →`}
            </TrackedButton>
          )}
        </Box>
      </Box>

      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          gap: 32,
          [theme.breakpoints.down("md")]: {
            flexDirection: "column-reverse",
            gap: 8
          }
        })}
      >
        <Button
          variant="contained"
          disabled={!!Env.IS_READ_ONLY_MODE}
          onClick={onJoinPlan}
          sx={{ width: 280 }}
        >
          {t`Purchase Membership`}
        </Button>

        {!requiresMembershipForEnrollment ? (
          <Button variant="text" size="medium" onClick={onContinueWithoutPlan}>
            {t`Continue Without Plan`}
          </Button>
        ) : (
          <Button
            variant="text"
            size="medium"
            onClick={onMorePlans}
            endIcon={<Icon icon={faArrowRight} />}
          >
            {t("Learn More")}
          </Button>
        )}
      </Box>

      <Typography variant="caption" sx={{ color: "grey.600" }}>
        {t(
          "Limited time offer for new members. Cannot be combined with other promotions."
        )}
      </Typography>
    </Box>
  );
}

export const usePolling = ({
  shouldStart,
  onTick,
  onTimeout,
  timeout = 20000,
  interval = 3000
}: {
  shouldStart: boolean;
  onTick: () => void;
  onTimeout: () => void;
  timeout?: number;
  interval?: number;
}) => {
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (shouldStart) {
      pollingIntervalRef.current = setInterval(onTick, interval);
      pollingTimeoutRef.current = setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        onTimeout();
      }, timeout);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [shouldStart, onTick, onTimeout, timeout, interval]);
};
